import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import axios from "../axiosConfig";
import Header from "./Header.jsx";
import Navbar from "./Navbar.jsx";
import TaskPanel from "./TaskPanel.jsx";
import { TaskRefreshContext } from "../context/TaskRefreshContext.jsx";
import { PageTitleSlotContext } from "../context/PageTitleContext.jsx";
import { setUserPermissions } from "../hooks/useUserPermissions";
import "./Layout.css";

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isStaging =
    (process.env.REACT_APP_ENVIRONMENT || "").toLowerCase() === "staging";
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
  const [rightPanelTaskIndicator, setRightPanelTaskIndicator] = useState(null);
  const [taskRefreshCount, setTaskRefreshCount] = useState(0);
  // The header's title element, which pages render their <PageTitle> into.
  const [pageTitleSlot, setPageTitleSlot] = useState(null);
  const refreshTasks = useCallback(() => setTaskRefreshCount((c) => c + 1), []);
  const [user, setUser] = useState(() => {
    // Try to load from localStorage on mount
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const PAGE_TITLES = {
      '/dashboard':        'Dashboard',
      '/jobs':             'Jobs',
      '/job':              'Job',
      '/session':          'Session',
      '/contacts':         'Contacts',
      '/contact':          'Contact',
      '/accounts':         'Accounts',
      '/account':          'Account',
      '/locations':        'Locations',
      '/tasks':            'Tasks',
      '/interactions':     'Interactions',
      '/slides':           'Slide Manager',
      '/programmebuilder': 'Builder',
      '/create-handouts':  'Create Handouts',
      '/export':           'Export',
      '/settings':         'Settings',
    };
    const path = '/' + (location.pathname.split('/').filter(Boolean)[0] || '');
    const label = PAGE_TITLES[path];
    const isStaging = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase() === 'staging';
    const appName = isStaging ? 'FT Portal (Staging)' : 'FT Portal';
    document.title = label ? `${label} - ${appName}` : appName;
  }, [location.pathname]);

  useEffect(() => {
    const checkIfLoggedIn = async () => {
      try {
        const response = await axios.get("/auth/session/verify/");
        // Share the permissions that come with this check, so the navbar renders
        // together with the page instead of waiting on its own request.
        setUserPermissions(response.data.permissions);
        setUser(response.data.user);
        localStorage.setItem("user", JSON.stringify(response.data.user));
      } catch (error) {
        setUserPermissions(null);
        localStorage.removeItem("user"); // clean up just in case
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    checkIfLoggedIn();
  }, [navigate]);


  const handleLogout = async () => {
    try {
      await axios.post("/auth/logout/", {}, { withCredentials: true });
      setUserPermissions(null); // the next user's come with their session check
      localStorage.removeItem("user"); // remove user on logout
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error.response?.data?.error || error.message);
    }
  };

  if (loading) {
    return <p>Checking authentication...</p>;
  }

  const handleToggleRightPanel = () => {
    setIsRightPanelOpen((prev) => !prev);
  };

  return (
    <div className="layout">
      {isStaging && <div className="staging-banner">STAGING SITE</div>}
      <div className="layout-body">
        <div className="layout-nav">
          <Navbar />
        </div>

        <div className="layout-right">
          <Header
            user={user}
            onLogout={handleLogout}
            onToggleRightPanel={handleToggleRightPanel}
            isRightPanelOpen={isRightPanelOpen}
            rightPanelTaskIndicator={rightPanelTaskIndicator}
            titleRef={setPageTitleSlot}
          />

          <TaskRefreshContext.Provider value={refreshTasks}>
            <PageTitleSlotContext.Provider value={pageTitleSlot}>
              <main className="layout-content">
                <Outlet />
              </main>
            </PageTitleSlotContext.Provider>
          </TaskRefreshContext.Provider>
        </div>

        <TaskPanel
          isOpen={isRightPanelOpen}
          user={user}
          loading={loading}
          onIndicatorChange={setRightPanelTaskIndicator}
          refreshCount={taskRefreshCount}
        />
      </div>
    </div>
  );
};

export default Layout;
